import {
  DIRECT_DEALS_LIST_VIEW,
  type DirectDealsListView,
  type UseDirectDealsList,
} from '@openx/components-direct-deals/lib/utils/context/types.ts';
import type { DirectDealsListFilters } from '@openx/components-direct-deals/types.ts';
import { type CriteriaChange, CriteriaDimension, SortDirection } from '@openx/types/tableCriteria.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import { adjustPaginationCriteria } from '@openx/utils/lib/tableHelpers.ts';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { TABLE_PAGE_SIZE } from '../../../config.ts';
import { type DefaultTableCriteria, useListCriteria } from '../../../utils/useListCriteria.ts';
import { useDirectDealsListFetch } from './useDirectDealsListFetch.ts';

const defaultCriteria: DefaultTableCriteria<'directDeals'> = {
  [CriteriaDimension.FILTERS]: {
    phrase: '',
  },
  [CriteriaDimension.PAGINATION]: {
    pageNumber: 1,
    pageSize: TABLE_PAGE_SIZE,
    totalCount: 100,
  },
  [CriteriaDimension.SORT]: {
    column: 'name',
    direction: SortDirection.ASC,
    sortableColumns: ['name', 'mln_impressions', 'mln_unique_visitors'],
  },
};

const localStorageKey = 'publisher_direct_deals_view';

export const useDirectDealsList: UseDirectDealsList = () => {
  const { criteria, setCriteria } = useListCriteria('directDeals', defaultCriteria);
  const [searchParams] = useSearchParams();

  const getInitialViewType = (): DirectDealsListView => {
    if (searchParams.get('resetViewType')) {
      window.localStorage.setItem(localStorageKey, DIRECT_DEALS_LIST_VIEW.TILE);
      searchParams.delete('resetViewType');

      return DIRECT_DEALS_LIST_VIEW.TILE;
    }

    const stored = window.localStorage.getItem(localStorageKey) as DirectDealsListView | null;

    return stored ?? DIRECT_DEALS_LIST_VIEW.TILE;
  };

  const [viewType, setViewType] = useState<DirectDealsListView>(getInitialViewType);

  const skip = !searchParams.has('tab');
  const directDeals = useDirectDealsListFetch({
    criteria,
    skip,
  });

  const totalCount = directDeals.tableCriteria.pagination?.totalCount;
  const requestedPage = criteria.pagination?.pageNumber ?? 1;

  useEffect(() => {
    if (skip || directDeals.loading || typeof totalCount !== 'number') {
      return;
    }

    setCriteria(previousCriteria => {
      const previousPage = previousCriteria.pagination?.pageNumber ?? 1;

      if (previousPage !== requestedPage) {
        return previousCriteria;
      }

      const adjustedCriteria = adjustPaginationCriteria(previousCriteria, totalCount, false);

      return isEqual(adjustedCriteria, previousCriteria) ? previousCriteria : adjustedCriteria;
    });
  }, [directDeals.loading, requestedPage, setCriteria, skip, totalCount]);

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, DirectDealsListFilters>) => {
      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);

      if (!isEqual(newCriteriaProcessed, criteria)) {
        setCriteria(newCriteriaProcessed);
      }
    },
    [criteria, setCriteria]
  );

  const handleChangeViewType = (type: DirectDealsListView) => {
    setViewType(previousViewType => {
      if (previousViewType !== type) {
        window.localStorage.setItem(localStorageKey, type);
      }

      return type;
    });
  };

  return {
    ...directDeals,
    viewType,
    handleCriteriaChange,
    handleChangeViewType,
  };
};
